"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DATE_TOKENS = exports.DATE_TOKEN_TO_LUXON = void 0;
exports.isDateToken = isDateToken;
exports.applyNamingCase = applyNamingCase;
exports.applyNamingSpaces = applyNamingSpaces;
exports.formatComponentValue = formatComponentValue;
exports.buildLevelSegments = buildLevelSegments;
exports.buildLevelName = buildLevelName;
exports.buildConventionNames = buildConventionNames;
exports.buildLevelFormat = buildLevelFormat;
/**
 * The single composition rule for a naming convention. Used by both the authoring preview and the
 * agent-facing prompt rendering, so what a user approves is what the agent is told.
 *
 * This module is intentionally dependency-free: `packages/shared` declares no runtime dependencies,
 * so date resolution is the caller's job (see {@link DATE_TOKEN_TO_LUXON}).
 */
/**
 * Display date tokens a date component may hold, mapped to their Luxon equivalents.
 *
 * The display tokens are NOT valid Luxon format strings — Luxon reads `DD` as a localized date
 * ("Jul 24, 2026") and does not know `YYYY` at all, so passing a display token straight to
 * `DateTime.toFormat` yields garbage. Anything outside this map is treated as literal text.
 */
exports.DATE_TOKEN_TO_LUXON = {
    'DD-MM-YYYY': 'dd-MM-yyyy',
    'DD-MM-YY': 'dd-MM-yy',
    'YYYY-MM-DD': 'yyyy-MM-dd',
    YYYYMMDD: 'yyyyMMdd',
};
/** The display tokens a date component may be set to, in the order the UI offers them. */
exports.DATE_TOKENS = Object.keys(exports.DATE_TOKEN_TO_LUXON);
/** Whether a value is a date format token this system knows how to resolve. */
function isDateToken(value) {
    return value in exports.DATE_TOKEN_TO_LUXON;
}
/** Applies a level's case setting. `Aa` means "as typed" and deliberately does not re-capitalize. */
function applyNamingCase(value, namingCase) {
    switch (namingCase) {
        case 'aa':
            return value.toLowerCase();
        case 'AA':
            return value.toUpperCase();
        case 'Aa':
            return value;
    }
}
/** Applies a level's whitespace policy. */
function applyNamingSpaces(value, spaces) {
    switch (spaces) {
        case 'nospace':
            return value.replace(/\s+/g, '');
        case 'with-dash':
            return value.replace(/\s+/g, '-');
        case 'keep':
            return value;
    }
}
/**
 * Formats one component value for inclusion in a name: case first, then whitespace policy.
 *
 * Case runs before the whitespace policy so that `AA` uppercases the whole phrase before its spaces
 * are collapsed; running it after would give the same result today but couples the two rules.
 */
function formatComponentValue(value, settings) {
    return applyNamingSpaces(applyNamingCase(value.trim(), settings.case), settings.spaces);
}
/**
 * The included components of a level, in order, each with its formatted value. Components with an
 * empty value are dropped: an empty segment would otherwise produce a doubled delimiter.
 */
function buildLevelSegments(level, resolvedDates = {}) {
    return level.components
        .filter((component) => component.included)
        .map((component) => {
        const resolved = resolvedDates[component.key];
        const rawValue = component.value.trim();
        const effectiveValue = resolved !== undefined && isDateToken(rawValue) ? resolved : rawValue;
        return {
            key: component.key,
            label: component.label,
            rawValue,
            formattedValue: formatComponentValue(effectiveValue, level),
        };
    })
        .filter((segment) => segment.formattedValue.length > 0);
}
/** Composes a level's name: included components in order, formatted, joined by the delimiter. */
function buildLevelName(level, resolvedDates = {}) {
    return buildLevelSegments(level, resolvedDates)
        .map((segment) => segment.formattedValue)
        .join(level.delimiter);
}
/** Composes all three level names from one convention. */
function buildConventionNames(convention, resolvedDates = {}) {
    return {
        campaign: buildLevelName(convention.campaign, resolvedDates),
        adSet: buildLevelName(convention.adSet, resolvedDates),
        ad: buildLevelName(convention.ad, resolvedDates),
    };
}
/**
 * The level's shape with component labels in place of values — `Product | Ad Format | Date`. Used in
 * the agent prompt so the model knows what belongs in each position, not just what the brand's own
 * example happened to contain.
 */
function buildLevelFormat(level) {
    return level.components
        .filter((component) => component.included)
        .map((component) => component.label)
        .join(level.delimiter);
}
